import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import useCatalog from '../lib/useCatalog';
import { useCatalogStatus } from '../lib/Catalog';
import { useLibrary } from '../lib/Library';
import { formatCount } from '../lib/format';
import { autoOrder, labelFor, routeFor, searchReleases, startBest } from '../lib/playback';
import Art from './Art';
import { useOpenTitle } from './PosterCard';
import CatalogNotice from './CatalogNotice';
import SourceLog from './SourceLog';
import ReleaseStatus, { TrailerButton, isNotOut, notOutCopy } from './ReleaseStatus';

// A release named as a trailer or teaser is one, whatever else it claims to be
const TRAILER_RELEASE = /(trailers?|teasers?)/i;

/**
 * Where this title is actually watchable in the configured region, as logos.
 *
 * Streaming logos link into Discover filtered to that service — "what else is
 * on there" is the natural next question. Rent/buy entries are named but not
 * linked: the browse filter deliberately means "included", not "for sale".
 * The strip credits JustWatch, whose data this is.
 */
const WhereToWatch = ({ watch }) => {
  if (!watch) return null;
  const paid = [...(watch.rent || []), ...(watch.buy || [])]
    .filter((entry, index, list) => list.findIndex((other) => other.id === entry.id) === index);

  return (
    <div className="watch">
      <span className="watch-k">WATCH ON</span>
      {watch.streaming.map((service) => (
        <Link
          key={service.id}
          to={`/discover?type=all&provider=${service.id}&sort=trending`}
          className="watch-service"
          title={`${service.name} — see what else is on it`}
        >
          {service.logo ? <img src={service.logo} alt={service.name} /> : service.name}
        </Link>
      ))}
      {!watch.streaming.length && paid.length > 0 && (
        <span className="watch-paid">
          rent or buy: {paid.slice(0, 4).map((entry) => entry.name).join(', ')}
        </span>
      )}
      <span className="watch-note">{watch.region} · data by JustWatch</span>
    </div>
  );
};

const YourRating = ({ title }) => {
  const { ratingFor, rate } = useLibrary();
  const score = ratingFor(title.type, title.id);

  return (
    <div className="stars">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={star <= (score || 0) ? 'is-set' : ''}
          aria-label={`Rate ${star} out of 5`}
          onClick={() => rate(title, star)}
        >
          ★
        </button>
      ))}
    </div>
  );
};

// TMDB names most seasons "Season 3", which the number already says. Only a
// real name (an anthology's "Fargo: Year Two", a "Specials" run) earns space.
const seasonLabel = (entry) => {
  if (entry.number === 0) return 'Specials';
  if (entry.name && !/^season\s*\d+$/i.test(entry.name.trim())) return entry.name;
  return `Season ${entry.number}`;
};

/**
 * The episode list, where every row is its own play button.
 *
 * There is no source list under this any more, so a row has nothing to select
 * *for* — pressing it is the whole interaction. `busy` is the key of whatever is
 * currently being started, so the row that was pressed is the one that says so.
 */
const Episodes = ({ title, season, onSeason, onPlay, busy }) => {
  const { data, loading } = useCatalog(`/api/catalog/title/series/${title.id}/season/${season}`);
  const seasons = title.seasons || [];
  const active = seasons.find((entry) => entry.number === season);

  return (
    <section className="md-section">
      <div className="md-section-head">
        <h3 className="md-section-title">Episodes</h3>
        <span className="md-section-note">
          {loading ? 'loading…' : data ? `${data.episodes.length} episodes${data.year ? ` · ${data.year}` : ''}` : ''}
        </span>
        {seasons.length > 1 ? (
          <label className="season-pick">
            {active ? seasonLabel(active) : `Season ${season}`}
            <span className="chev chev--down" />
            <select value={season} onChange={(event) => onSeason(Number(event.target.value))} aria-label="Season">
              {seasons.map((entry) => (
                <option key={entry.number} value={entry.number}>
                  {seasonLabel(entry)}
                  {entry.episodeCount ? ` · ${entry.episodeCount} episodes` : ''}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {loading ? (
        <div className="loading">LOADING EPISODES</div>
      ) : (
        <div className="episodes">
          {(data?.episodes || []).map((entry) => {
            const key = `s${season}e${entry.number}`;
            const starting = busy === key;

            return (
              <button
                key={entry.number}
                type="button"
                className={`episode${starting ? ' is-starting' : ''}`}
                onClick={() => onPlay(season, entry.number, entry.title)}
                disabled={Boolean(busy)}
                aria-label={`Play episode ${entry.number}, ${entry.title}`}
              >
                <span className="episode-num">{entry.number}</span>
                <span className="episode-thumb">
                  <Art src={entry.still} alt="" label="STILL" className="episode-still" />
                  <span className="episode-scrim">
                    {starting ? <span className="play-spin" /> : <span className="play-tri play-tri--sm" />}
                  </span>
                </span>
                <span className="episode-main">
                  <span className="episode-title">{entry.title}</span>
                  <span className="episode-syn">{entry.overview || 'No synopsis yet.'}</span>
                </span>
                <span className="episode-right">
                  {starting ? 'starting…' : [entry.runtime, entry.airDate].filter(Boolean).join(' · ')}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
};

/** "More like this": three landscape tiles with a line of meta and a synopsis. */
const Similar = ({ items }) => {
  const openTitle = useOpenTitle();
  return (
    <section className="md-section">
      <div className="md-section-head">
        <h3 className="md-section-title">More like this</h3>
      </div>
      <div className="similar">
        {items.slice(0, 6).map((similar) => (
          <a
            key={`${similar.type}-${similar.id}`}
            href={`/title/${similar.type}/${similar.id}`}
            className="similar-card"
            onClick={(event) => {
              event.preventDefault();
              openTitle(similar);
            }}
          >
            <span className="similar-art">
              {(similar.backdrop || similar.poster) && <img src={similar.backdrop || similar.poster} alt="" loading="lazy" />}
              <span className="similar-name">{similar.name}</span>
            </span>
            <span className="similar-body">
              <span className="similar-meta">
                {similar.rating ? <span className="pv-star">★ {similar.rating}</span> : null}
                {similar.year ? <span>{similar.year}</span> : null}
                <span>{similar.type === 'series' ? 'Series' : 'Film'}</span>
              </span>
              {similar.overview ? <span className="similar-desc">{similar.overview}</span> : null}
            </span>
          </a>
        ))}
      </div>
    </section>
  );
};

/**
 * A title, as a modal over the page it was opened from.
 *
 * Opened from a card it sits over the rail you were browsing; opened by URL it
 * sits over an empty shell and closing it goes home. Everything the old title
 * page did happens here — the play button still searches the indexers, ranks
 * what comes back and opens the first release that actually opens.
 */
const Title = () => {
  const { type, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: title, loading, error, notConfigured } = useCatalog(`/api/catalog/title/${type}/${id}`);
  const { has, toggle } = useLibrary();
  const { can } = useCatalogStatus();
  const [season, setSeason] = useState(null);
  // The key of whatever is being started ('title', or 's2e4'), so exactly one
  // control can say it is working and the rest go inert rather than queueing up
  // three searches for three impatient presses.
  const [busy, setBusy] = useState(null);
  // Why the last play produced nothing: {kind: 'none' | 'failed', reason}.
  // Null until a play fails; cleared by the next one.
  const [failure, setFailure] = useState(null);
  // The trace of the last failed play, shown under the error. Only set on a
  // failure; a play that starts navigates away and needs no explaining.
  const [sourceLog, setSourceLog] = useState(null);
  const autoplayed = useRef(false);

  const isModal = Boolean(location.state?.background);
  const close = useCallback(() => {
    if (isModal) navigate(-1);
    else navigate('/home');
  }, [isModal, navigate]);

  /**
   * On a phone the title is a sheet (mobile.css), and a sheet is dismissed by
   * pulling it down: from anywhere on it, so long as it is scrolled to its top,
   * far enough or fast enough. Touch events rather than pointer events, because
   * the scrim is a native scroller and only a non-passive touchmove can keep
   * the browser from turning that pull into an overscroll.
   */
  const scrimRef = useRef(null);
  const sheetRef = useRef(null);
  useEffect(() => {
    const scrim = scrimRef.current;
    const sheet = sheetRef.current;
    if (!scrim || !sheet) return undefined;
    let pull = null;

    const onStart = (event) => {
      if (!window.matchMedia('(max-width: 760px)').matches || event.touches.length !== 1) return;
      // A horizontal rail inside the sheet keeps its own touches
      if (event.target.closest?.('.rail, .ep-list, input, textarea')) return;
      pull = scrim.scrollTop <= 0 ? { y: event.touches[0].clientY, t: event.timeStamp, dy: 0, live: false } : null;
    };
    const onMove = (event) => {
      if (!pull) return;
      const dy = event.touches[0].clientY - pull.y;
      if (!pull.live) {
        if (dy < 6) {
          if (dy < -6) pull = null;
          return;
        }
        pull.live = true;
      }
      event.preventDefault();
      pull.dy = dy;
      sheet.style.transition = 'none';
      sheet.style.transform = `translateY(${Math.max(0, dy)}px)`;
    };
    const onEnd = (event) => {
      if (!pull?.live) {
        pull = null;
        return;
      }
      const { dy, t } = pull;
      pull = null;
      sheet.style.transition = '';
      const velocity = dy / Math.max(1, event.timeStamp - t);
      if (dy > 130 || velocity > 0.7) {
        sheet.style.transform = 'translateY(100%)';
        window.setTimeout(close, 180);
      } else {
        sheet.style.transform = '';
      }
    };

    scrim.addEventListener('touchstart', onStart, { passive: true });
    scrim.addEventListener('touchmove', onMove, { passive: false });
    scrim.addEventListener('touchend', onEnd);
    scrim.addEventListener('touchcancel', onEnd);
    return () => {
      scrim.removeEventListener('touchstart', onStart);
      scrim.removeEventListener('touchmove', onMove);
      scrim.removeEventListener('touchend', onEnd);
      scrim.removeEventListener('touchcancel', onEnd);
    };
  }, [close]);

  // The page behind stays put while the modal scrolls on its own
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  // A new title in the same modal starts from its first season again
  useEffect(() => setSeason(null), [type, id]);

  // Series land on the first season, because the episode list has to show one
  useEffect(() => {
    if (title?.type === 'series' && title.seasons?.length && season == null) {
      setSeason(title.seasons[0].number);
    }
  }, [title, season]);

  /**
   * Play, without asking anyone to read a torrent list.
   *
   * Search, order the results for automatic playback, and open the first one
   * that actually opens. From here on quality is the player's business — it has
   * the same list and a menu to move between resolutions mid-film.
   */
  const play = useCallback(
    async ({ season: wantSeason, episode, episodeName, key, replace = false }) => {
      setBusy(key);
      setFailure(null);
      setSourceLog(null);

      const isEpisode = episode != null;
      const scope = isEpisode
        ? `S${wantSeason} E${episode}${episodeName ? ` · ${episodeName}` : ''}`
        : title.name;
      // Hoisted so the catch can build the log from whatever the attempt reached
      // before it failed — the search meta, and each release playback tried.
      const attempts = [];
      let meta = null;
      let found = 0;

      // Reads `meta`/`found` at call time, so it picks up whatever the attempt
      // has reached by the point it runs.
      const showLog = (pending) =>
        setSourceLog({ label: scope, isEpisode, meta, found, attempts: [...attempts], pending });

      try {
        const search = await searchReleases({
          name: title.name,
          originalName: title.originalName,
          originalLanguage: title.originalLanguage,
          type: title.type,
          imdbId: title.imdbId,
          year: title.year,
          season: wantSeason,
          episode
        });
        meta = search.meta;
        found = search.results.length;

        if (!found) {
          setFailure({
            kind: 'none',
            scope: isEpisode ? 'episode' : title.type === 'series' ? 'series' : 'film',
            retry: { season: wantSeason, episode, episodeName, key }
          });
          setSourceLog({ label: scope, isEpisode, meta, found: 0, attempts: [] });
          return;
        }

        const { release, target } = await startBest(
          // Runtime is what turns a file size into a bitrate, and so what
          // decides whether a swarm can keep up with it
          autoOrder(search.results, { type: title.type, runtimeMinutes: title.runtimeMinutes }),
          {
            debrid: meta?.debrid,
            season: wantSeason,
            episode,
            onAttempt: (entry) => {
              const at = attempts.findIndex((a) => a.seq === entry.seq);
              if (at === -1) attempts.push(entry);
              else attempts[at] = entry;

              // Shown as it happens rather than assembled in the catch. Three
              // dead swarms is three peer-finding timeouts back to back, and
              // holding the log until the end made that a spinner with nothing
              // beside it for minutes — the thing this log exists to prevent.
              showLog(true);
            }
          }
        );

        navigate(routeFor(target), {
          state: {
            ...labelFor(title, { season: wantSeason, episode, episodeName, release }),
            // For the player's own honesty: a file named as a trailer is
            // labelled one, a film still in cinemas is flagged as not its
            // home release, and the official trailer rides along for when
            // playback dies.
            isTrailer: TRAILER_RELEASE.test(release?.title || release?.name || ''),
            prerelease: isNotOut(title) ? notOutCopy(title).kicker : null,
            trailer: title.trailer || null
          },
          // Nobody chose to be on this page — they pressed Play somewhere else
          // and it was a stop on the way. Leaving it in the history means Back
          // lands on a page that would immediately autoplay them forward again.
          replace
        });
      } catch (err) {
        setFailure({
          kind: 'failed',
          reason: err.response?.data?.error || 'Could not start playback for this title.',
          retry: { season: wantSeason, episode, episodeName, key }
        });
        showLog(false);
      } finally {
        setBusy(null);
      }
    },
    [navigate, title]
  );

  // Arriving from a "Play" elsewhere in the app means playing, not landing on a
  // page with a button on it. A series starts at its first episode, which is the
  // same thing the hero button on this page offers.
  useEffect(() => {
    if (autoplayed.current || !location.state?.autoplay || !title) return;

    const first = title.type === 'series' ? title.seasons?.[0]?.number : null;
    if (title.type === 'series' && first == null) return;

    autoplayed.current = true;
    // A film still in cinemas has nothing worth playing; arriving here from a
    // Play elsewhere lands on the page that explains why, not on a search
    // that can only come back empty or wrong.
    const skip = isNotOut(title);
    // Spend the flag before acting on it. It lives in the history entry, so a
    // search that fails and leaves the viewer here would otherwise autoplay
    // again the next time this entry is visited.
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: { ...location.state, autoplay: false }
    });

    if (skip) return;
    play(
      title.type === 'series'
        ? { season: first, episode: 1, key: 'title', replace: true }
        : { key: 'title', replace: true }
    );
  }, [location.state, location.pathname, location.search, navigate, title, play]);

  let body;
  if (loading) {
    body = <div className="modal-empty loading">LOADING TITLE</div>;
  } else if (notConfigured) {
    body = (
      <div className="modal-empty">
        <CatalogNotice />
      </div>
    );
  } else if (error) {
    body = <div className="modal-empty alert">{error}</div>;
  } else {
    const isSeries = title.type === 'series';
    const inList = has(title.type, title.id);
    const cast = title.cast || [];
    const notOut = isNotOut(title);
    const statusLabel = notOut ? notOutCopy(title).kicker : title.status;

    // For a series the hero button is a way in, not a choice: the first episode
    // of the season on screen. Anything else is one press away in the list.
    const heroPlay = () =>
      play(
        isSeries
          ? { season, episode: 1, key: 'title' }
          : { key: 'title' }
      );

    body = (
      <>
        <div className="md-art">
          {title.backdrop && <img src={title.backdrop} alt="" />}
          <span className="md-art-fade" />
          <div className="md-head">
            <div className="kicker">
              {isSeries
                ? `SERIES · ${title.seasonCount} SEASON${title.seasonCount === 1 ? '' : 'S'}`
                : 'FILM'}
              {statusLabel ? (
                <>
                  <span className="kicker-rule" />
                  <span className="kicker-alt">{statusLabel.toUpperCase()}</span>
                </>
              ) : null}
            </div>
            <h2 className="md-name">{title.name}</h2>
            <div className="md-actions">
              {/* Not out: the trailer leads, labelled as a trailer, and the
                  search moves into the status panel as a deliberate choice */}
              {notOut && title.trailer ? (
                <TrailerButton trailer={title.trailer} primary className="md-trailer" />
              ) : (
              <button
                type="button"
                className="md-play"
                onClick={heroPlay}
                disabled={Boolean(busy) || (isSeries && season == null)}
              >
                {busy === 'title' ? <span className="play-spin" /> : <span className="play-tri play-tri--dark" />}
                {busy === 'title'
                  ? 'Finding the best source…'
                  : isSeries
                    ? `Play S${season ?? 1} E1`
                    : 'Play'}
              </button>
              )}
              <button
                type="button"
                className={`pv-ring pv-ring--lg${inList ? ' is-on' : ''}`}
                aria-label={inList ? 'Remove from my list' : 'Add to my list'}
                onClick={() => toggle(title)}
              >
                <span className={`plus${inList ? ' plus--check' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        <div className="md-body">
          <div className="md-main">
            <div className="meta-line">
              {title.community?.rating ? (
                <span className="meta-star">
                  ★ {title.community.rating}
                  {title.community.votes ? <span className="meta-dim"> · {formatCount(title.community.votes)}</span> : null}
                </span>
              ) : null}
              {isSeries ? (
                <span>
                  {title.firstAirYear}
                  {title.lastAirYear && title.lastAirYear !== title.firstAirYear ? `–${title.lastAirYear}` : ''}
                </span>
              ) : (
                title.year && <span>{title.year}</span>
              )}
              {title.certification && <span className="cert">{title.certification}</span>}
              {isSeries && title.episodeCount ? <span>{title.episodeCount} episodes</span> : null}
              {title.runtime && <span>{title.runtime}</span>}
              <span className="cert cert--hd">HD</span>
            </div>
            {title.tagline && <p className="md-tagline">{title.tagline}</p>}
            {title.overview && <p className="md-desc">{title.overview}</p>}
            {notOut ? (
              <ReleaseStatus
                title={title}
                kind="not-out"
                reason={failure
                  ? failure.kind === 'none'
                    ? 'A search just now found nothing playable, as expected until then.'
                    : 'A search just now found releases, but none of them would start.'
                  : null}
                onRetry={heroPlay}
                retrying={busy === 'title'}
              />
            ) : failure ? (
              <ReleaseStatus
                title={title}
                kind={failure.kind}
                scope={failure.scope}
                reason={failure.reason}
                onRetry={() => play(failure.retry)}
                retrying={Boolean(busy)}
              />
            ) : null}
            <SourceLog log={sourceLog} />
            <WhereToWatch watch={title.watch} />
          </div>

          <div className="md-side">
            {cast.length > 0 && (
              <div>
                <span className="md-k">Cast: </span>
                {cast.slice(0, 6).map((person, index) => (
                  <React.Fragment key={person.id || person.name}>
                    {index > 0 ? ', ' : ''}
                    {can('people') && person.id ? (
                      <Link to={`/person/${person.id}`} className="md-link">
                        {person.name}
                      </Link>
                    ) : (
                      person.name
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
            {title.genres?.length ? (
              <div>
                <span className="md-k">Genres: </span>
                {title.genres.join(' · ')}
              </div>
            ) : null}
            {(title.facts || []).map((fact) => (
              <div key={fact.k}>
                <span className="md-k">{fact.k.charAt(0) + fact.k.slice(1).toLowerCase()}: </span>
                {fact.v}
              </div>
            ))}
            <div className="md-rating">
              <span className="md-k">Your rating: </span>
              <YourRating title={title} />
            </div>
          </div>

          {isSeries && season != null && (
            <Episodes
              title={title}
              season={season}
              onSeason={setSeason}
              busy={busy}
              onPlay={(wantSeason, episode, episodeName) =>
                play({ season: wantSeason, episode, episodeName, key: `s${wantSeason}e${episode}` })
              }
            />
          )}

          {title.similar?.length > 0 && <Similar items={title.similar} />}
        </div>
      </>
    );
  }

  return (
    <div ref={scrimRef} className="modal-scrim" onClick={close} role="presentation">
      <div
        ref={sheetRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title?.name || 'Title'}
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="modal-close" aria-label="Close" onClick={close}>
          ×
        </button>
        {body}
      </div>
    </div>
  );
};

export default Title;
